import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";

Font.register({
  family: "Georama",
  fonts: [
    { src: "/fonts/Georama-Bold.ttf", fontWeight: "bold" },
    { src: "/fonts/Georama-SemiBold.ttf", fontWeight: "semibold" },
    { src: "/fonts/Georama-Regular.ttf", fontWeight: "normal" },
    { src: "/fonts/Georama-Medium.ttf", fontWeight: "medium" },
    { src: "/fonts/Georama-Light.ttf", fontWeight: "light" },
  ],
});

// Create styles
const styles = StyleSheet.create({
  // ===== PAGE STYLING =====
  page: {
    padding: 40,
    fontFamily: "Georama",
    fontSize: 12,
  },

  // ===== HEADER STYLING =====
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 30,
  },
  companyInfo: {
    maxWidth: "50%",
  },
  companyName: {
    fontSize: 20,
    fontWeight: "semibold",
    marginBottom: 10,
  },
  companyAddress: {
    fontSize: 12,
    fontWeight: "light",
    color: "#333",
  },
  logo: {
    width: 100,
    height: 40,
    objectFit: "contain",
  },
  title: {
    fontSize: 18,
    fontWeight: "bold",
    maxWidth: "50%",
    textAlign: "left",
  },
  instructionTitle: {
    fontSize: 16,
    fontWeight: "semibold",
    maxWidth: "48%",
    textAlign: "left",
  },

  // ===== FORM STYLING =====
  formRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 0,
    alignItems: "stretch",
  },
  formColumn: {
    width: "48%",
    display: "flex",
    flexDirection: "column",
  },
  label: {
    fontSize: 12,
    fontWeight: "semibold",
    marginBottom: 5,
  },
  input: {
    height: 30,
    border: "1px solid #ccc",
    marginBottom: 10,
    padding: 5,
    paddingLeft: 8,
    fontWeight: "normal",
  },
  largeInput: {
    height: 80,
    border: "1px solid #ccc",
    marginBottom: 10,
    padding: 5,
    paddingTop: 7,
    paddingLeft: 8,
    fontWeight: "normal",
  },
  bankAddressLargeInput: {
    flexGrow: 1,
    border: "1px solid #ccc",
    marginBottom: 10,
    padding: 5,
    paddingTop: 7,
    paddingLeft: 8,
    fontWeight: "normal",
    minHeight: 106,
  },
  instructionText: {
    fontSize: 10,
    fontWeight: "light",
    marginBottom: 10,
    lineHeight: 1.4,
  },

  // ===== SERVICE USER NUMBER STYLING =====
  serviceNumberContainer: {
    flexDirection: "row",
    marginBottom: 10,
  },
  serviceNumberBox: {
    width: 30,
    height: 30,
    border: "1px solid #ccc",
    marginRight: 5,
    justifyContent: "center",
    alignItems: "center",
  },
  serviceNumberText: {
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "center",
  },

  // ===== FOOTER STYLING =====
  footer: {
    fontSize: 8,
    color: "#999",
    marginTop: 10,
    textAlign: "center",
  },

  // ===== DIVIDER STYLING =====
  divider: {
    borderBottom: "1px solid #ccc",
    marginVertical: 15,
    marginBottom: 10,
  },

  // ===== GUARANTEE SECTION STYLING =====
  guaranteeSection: {
    marginBottom: 0,
  },
  guaranteeHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  guaranteeTitle: {
    fontSize: 16,
    fontWeight: "semibold",
  },
  bulletPoint: {
    flexDirection: "row",
    marginBottom: 10,
  },
  bullet: {
    width: 10,
    marginRight: 5,
  },
  bulletText: {
    flex: 1,
    fontSize: 10,
    lineHeight: 1.4,
  },
});

// Define the data interface
interface DirectDebitData {
  // Company information
  originatorCompanyName?: string;
  originatorCompanyAddress1?: string;
  originatorCompanyAddress2?: string;
  ddLogo?: string;
  yavLogo?: string;
  signature?: string;

  // Form fields
  accountHolderName?: string;
  bankAccountNo?: string;
  sortCode?: string;
  bankAddress?: string;
  reference?: string;
  date?: string;
  name?: string;
  email?: string;

  // Service user number
  serviceUserNumber?: string;

  // Guarantee text
  guaranteeText?: {
    title?: string;
    points?: string[];
  };

  // Instructions
  instructionTitle?: string;
  instructionText?: string[];

  // Footer text
  footerText?: string;
}

// Default data
const defaultData: DirectDebitData = {
  originatorCompanyName: "Yavrio Ltd.",
  originatorCompanyAddress1: "86-90 Paul Street, London,",
  originatorCompanyAddress2: "England, SW1 4RF",
  ddLogo: "/dd-logo.jpg",
  signature: "",
  yavLogo: "",
  serviceUserNumber: "123456",
  instructionTitle:
    "Instruction to your bank or building society to pay by Direct Debit",
  instructionText: [
    "Please pay [COMPANY_NAME] Direct Debits from the account detailed in this instruction subject to the safeguards assured by the Direct Debit Guarantee. I understand that this instruction may remain with [COMPANY_NAME] and, if so, details will be passed electronically to my bank/building society.",
  ],
  guaranteeText: {
    title: "The Direct Debit Guarantee",
    points: [
      "This Guarantee is offered by all banks and building societies that accept instructions to pay Direct Debits.",
      "If there are any changes to the amount, date or frequency of your Direct Debit, [COMPANY_NAME] will notify you 3 working days in advance of your account being debited or as otherwise agreed. If you request [COMPANY_NAME] to collect a payment, confirmation of the amount and date will be given to you at the time of the request.",
      "If an error is made in the payment of your Direct Debit, by [COMPANY_NAME] or your bank or building society, you are entitled to a full and immediate refund of the amount paid from your bank or building society – if you receive a refund you are not entitled to, you must pay it back when [COMPANY_NAME] asks you to.",
      "You can cancel a Direct Debit at any time by simply contacting your bank or building society. Written confirmation may be required. Please also notify us.",
    ],
  },
  footerText:
    "Banks and building societies may not accept Direct Debit Instructions for some types of account",
};

// Create Document Component
const DirectDebitMandatePDF = ({
  data = {},
}: {
  data: Partial<DirectDebitData>;
}) => {
  // Merge default data with provided data
  const mergedData = { ...defaultData, ...data };

  // Get the company name for dynamic replacement
  const companyName = mergedData.originatorCompanyName || "Yavrio Ltd.";

  // Extract service user number digits
  const serviceUserNumberDigits = (mergedData.serviceUserNumber || "").split(
    ""
  );

  return (
    <Document
      title={`${mergedData.reference} - ${companyName} Direct Debit Mandate`}
      author={companyName}
    >
      <Page size="A4" style={styles.page}>
        {/* Header */}

        {/* <View style={styles.header}>
          <Image style={styles.logo} src={mergedData.yavLogo} />
          <Image style={styles.logo} src={mergedData.ddLogo} />
        </View> */}
        <View style={styles.header}>
          <View style={styles.companyInfo}>
            <Text style={styles.companyName}>
              {mergedData.originatorCompanyName}
            </Text>
            <Text style={styles.companyAddress}>
              {mergedData.originatorCompanyAddress1}
            </Text>
            <Text style={styles.companyAddress}>
              {mergedData.originatorCompanyAddress2}
            </Text>
          </View>
          <Text style={styles.instructionTitle}>
            {mergedData.instructionTitle}
          </Text>
        </View>

        {/* Form Section */}
        <View style={styles.formRow}>
          <View style={styles.formColumn}>
            <Text style={styles.label}>Company Name</Text>
            <View style={styles.input}>
              <Text>{mergedData.name}</Text>
            </View>

            <Text style={styles.label}>Name of account holder</Text>
            <View style={styles.input}>
              <Text>{mergedData.accountHolderName}</Text>
            </View>

            <Text style={styles.label}>
              Bank/Building Society account number
            </Text>
            <View style={styles.input}>
              <Text>{mergedData.bankAccountNo}</Text>
            </View>

            <Text style={styles.label}>Branch sort code</Text>
            <View style={styles.input}>
              <Text>{mergedData.sortCode}</Text>
            </View>

            <Text style={styles.label}>
              Name and full postal address of your Bank/Building Society
            </Text>
            <View style={styles.bankAddressLargeInput}>
              <Text>{mergedData.bankAddress}</Text>
            </View>
          </View>

          <View style={styles.formColumn}>
            <Text style={styles.label}>Service User Number</Text>
            <View style={styles.serviceNumberContainer}>
              {serviceUserNumberDigits.map((digit, index) => (
                <View key={index} style={styles.serviceNumberBox}>
                  <Text style={styles.serviceNumberText}>{digit}</Text>
                </View>
              ))}
            </View>

            <Text style={styles.label}>Reference</Text>
            <View style={styles.input}>
              <Text>{mergedData.reference}</Text>
            </View>

            <Text style={styles.label}>
              Instruction to your Bank or Building Society
            </Text>
            {mergedData.instructionText?.map((text, index) => (
              <Text key={index} style={styles.instructionText}>
                {text.replace(/\[COMPANY_NAME\]/g, companyName)}
              </Text>
            ))}

            <Text style={styles.label}>Signature(s)</Text>
            <View style={styles.largeInput}>
              <Text>{mergedData.signature} </Text>
            </View>

            <Text style={styles.label}>Date</Text>
            <View style={styles.input}>
              <Text>{mergedData.date}</Text>
            </View>
          </View>
        </View>

        <Text style={styles.footer}>{mergedData.footerText}</Text>

        <View style={styles.divider} />

        {/* Guarantee Section - Modified to use full width */}
        <View style={styles.guaranteeSection}>
          <View style={styles.guaranteeHeader}>
            <Text style={styles.guaranteeTitle}>
              {mergedData.guaranteeText?.title}
            </Text>
            <Image style={styles.logo} src={mergedData.ddLogo} />
          </View>

          {/* Bullet points now use full width */}
          {mergedData.guaranteeText?.points?.map((point, index) => (
            <View key={index} style={styles.bulletPoint}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>
                {point.replace(/\[COMPANY_NAME\]/g, companyName)}
              </Text>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

export default DirectDebitMandatePDF;
